/*
 *
 * Copyright (c) 1999-2022 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {IconFactory} from "@luciad/ria-sample-common/IconFactory";
import {Feature} from "@luciad/ria/model/feature/Feature";
import {Shape} from "@luciad/ria/shape/Shape";
import {FeaturePainter, PaintState} from "@luciad/ria/view/feature/FeaturePainter";
import {Layer} from "@luciad/ria/view/Layer";
import {Map} from "@luciad/ria/view/Map";
import {GeoCanvas} from "@luciad/ria/view/style/GeoCanvas";

function getEarthquakeColor(depth: number): string {
  if (depth < 10) {
    return "rgba(177, 0, 38, 0.7)";
  } else if (depth < 25) {
    return "rgba(227, 27, 28, 0.7)";
  } else if (depth < 50) {
    return "rgba(252, 78, 42, 0.7)";
  } else if (depth < 100) {
    return "rgba(253, 141, 60, 0.7)";
  } else if (depth < 250) {
    return "rgba(254, 178, 76, 0.7)";
  } else if (depth < 400) {
    return "rgba(224, 217, 118, 0.7)";
  } else if (depth < 500) {
    return "rgba(200, 237, 160, 0.7)";
  } else {
    return "rgba(170, 255, 204, 0.7)";
  }
}

/**
 * FeaturePainter that displays dot icons with a color and size based on properties in the GML file:
 * <ul>
 *   <li>The "Magnitude" property determines the dot size: larger dots for stronger quakes</li>
 *   <li>The "Depth" property determines the dot color: superficial quakes are red, deep quakes are yellow to blue</li>
 * </ul>
 */
export class EarthquakesPainter extends FeaturePainter {

  private readonly _iconCache: Record<string, HTMLCanvasElement>;

  constructor() {
    super();
    this._iconCache = {};
  }

  paintBody(geoCanvas: GeoCanvas, feature: Feature, shape: Shape, layer: Layer, map: Map,
            paintState: PaintState): void {
    const dimension = Math.pow(1.5, Math.round(feature.properties.Magnitude));
    const outlineColor = "rgba(255,255,255,1)";
    const fillColor = paintState.selected ? "rgba(192, 217, 42, 1)" : getEarthquakeColor(feature.properties.Depth);
    const cacheKey = `${dimension}${outlineColor}${fillColor}`;
    let icon = this._iconCache[cacheKey];
    if (!icon) {
      icon = this._iconCache[cacheKey] = IconFactory.circle({
        stroke: outlineColor,
        fill: fillColor,
        width: dimension,
        height: dimension
      });
    }

    geoCanvas.drawIcon(shape.focusPoint!, {
      width: `${dimension}px`,
      height: `${dimension}px`,
      image: icon
    });
  }
}